import { addDaysYmd, minutesOfDayInZone, ymdOfInstant } from "../lib/zone.js";
import type { IntakeFieldDef } from "../lib/intakeFields.js";

/**
 * Pure helpers for the public booking page: grouping times by the business's local day, the staff each visit can be with,
 * the short-lived "verified" session kept by this browser tab, and the words for every error the booking function can give.
 */

export interface PublicService {
  id: string;
  name: string;
  /** Services are grouped by this on the booking page; null/absent = none. */
  category?: string | null;
  duration_minutes: number;
  price_cents: number | null;
  price_from: boolean | null;
  intake_fields: IntakeFieldDef[] | null;
}

export interface PublicResource {
  id: string;
  name: string;
  service_ids: string[];
}

export interface PublicConfig {
  name: string;
  /** "12 High St, Fitzroy, Victoria 3065" (whatever the business has filled in), or null. */
  address: string | null;
  /** The number callers ring (E.164), or null. */
  phone: string | null;
  timezone: string;
  min_notice_minutes: number;
  max_days_ahead: number;
  change_notice_hours: number;
  sends_confirmation: boolean;
  turnstile_site_key: string | null;
  services: PublicService[];
  resources: PublicResource[];
}

export interface Slot {
  starts_at: string;
  ends_at: string;
  resource_ids: string[];
}

/** Times grouped by the business-local day ("2026-10-06"), earliest first, keeping only times the chosen staff member (null = anyone) is free. */
export function groupSlotsByDay(slots: Slot[], zone: string, resourceId: string | null): Map<string, Slot[]> {
  const byDay = new Map<string, Slot[]>();
  const sorted = [...slots].sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at));
  for (const slot of sorted) {
    if (resourceId && !slot.resource_ids.includes(resourceId)) continue;
    const day = ymdOfInstant(slot.starts_at, zone);
    const list = byDay.get(day);
    if (list) list.push(slot);
    else byDay.set(day, [slot]);
  }
  return byDay;
}

/** For one day's times: staff member -> (minute of the business-local day a visit can start -> that time). */
export function startsByResource(daySlots: Slot[], zone: string): Map<string, Map<number, Slot>> {
  const out = new Map<string, Map<number, Slot>>();
  for (const slot of daySlots) {
    const minute = minutesOfDayInZone(slot.starts_at, zone);
    for (const id of slot.resource_ids) {
      const byMinute = out.get(id) ?? new Map<number, Slot>();
      byMinute.set(minute, slot);
      out.set(id, byMinute);
    }
  }
  return out;
}

/** The hours the grid shows: from the earliest start to the latest finish over every loaded time, to whole hours (9 am to 5 pm when there are none). */
export function slotRange(slots: Slot[], zone: string, durationMin: number): { start: number; end: number } {
  if (slots.length === 0) return { start: 9 * 60, end: 17 * 60 };
  let lo = Infinity;
  let hi = -Infinity;
  for (const s of slots) {
    const m = minutesOfDayInZone(s.starts_at, zone);
    lo = Math.min(lo, m);
    hi = Math.max(hi, m + durationMin);
  }
  return { start: Math.floor(lo / 60) * 60, end: Math.min(24 * 60, Math.ceil(hi / 60) * 60) };
}

/** The stretches of the day a visit of this length can happen in, given every start that works (neighbouring ones are joined). */
export function openBlocks(starts: number[], durationMin: number): Array<{ startMin: number; endMin: number }> {
  const blocks: Array<{ startMin: number; endMin: number }> = [];
  for (const start of [...starts].sort((a, b) => a - b)) {
    const last = blocks[blocks.length - 1];
    if (last && start <= last.endMin) last.endMin = Math.max(last.endMin, start + durationMin);
    else blocks.push({ startMin: start, endMin: start + durationMin });
  }
  return blocks;
}

/** The first local day after `afterYmd` that has a time (null if none is loaded). */
export function nextDayWithSlots(slots: Slot[], zone: string, afterYmd: string): string | null {
  let best: string | null = null;
  for (const s of slots) {
    const day = ymdOfInstant(s.starts_at, zone);
    if (day > afterYmd && (best === null || day < best)) best = day;
  }
  return best;
}

/** Adds newly loaded times to those already shown (a time loaded twice is kept once). */
export function mergeSlots(existing: Slot[], incoming: Slot[]): Slot[] {
  const seen = new Map(existing.map((s) => [s.starts_at, s]));
  for (const s of incoming) seen.set(s.starts_at, s);
  return [...seen.values()];
}

/** The last word of a name ("Alex Smith" -> "smith"); a one-word name sorts as itself. */
export function surnameKey(name: string): string {
  const words = name.trim().split(/\s+/);
  return (words[words.length - 1] ?? "").toLowerCase();
}

/** Staff by surname, then by full name (so two Smiths are in first-name order). */
export function sortBySurname<T extends { name: string }>(people: T[]): T[] {
  return [...people].sort((a, b) => surnameKey(a.name).localeCompare(surnameKey(b.name), "en-AU") || a.name.localeCompare(b.name, "en-AU"));
}

/** Staff who can do every one of the chosen services, sorted by surname (the columns of the day view). */
export function staffForServices(resources: PublicResource[], serviceIds: string[]): PublicResource[] {
  return sortBySurname(resources.filter((r) => serviceIds.every((id) => r.service_ids.includes(id))));
}

/** When moving a booking: keep the same staff member if they are free at the new time, otherwise anyone who is. */
export function pickResource(slot: Slot, preferred: string | null): string | null {
  if (preferred && slot.resource_ids.includes(preferred)) return preferred;
  return slot.resource_ids[0] ?? null;
}

/** The last local day a window of `days` days starting at `fromYmd` covers, and whether more can still be asked for. */
export function nextWindow(fromYmd: string, days: number, todayYmd: string, maxDaysAhead: number): { to: string; more: boolean; next: string } {
  const to = addDaysYmd(fromYmd, days - 1);
  const last = addDaysYmd(todayYmd, maxDaysAhead);
  const next = addDaysYmd(fromYmd, days);
  return { to, more: next <= last, next };
}

/** The extra questions of the chosen services, each question once (a required one stays required). */
export function questionsFor(services: PublicService[], serviceIds: string[]): IntakeFieldDef[] {
  const out = new Map<string, IntakeFieldDef>();
  for (const s of services) {
    if (!serviceIds.includes(s.id)) continue;
    for (const f of s.intake_fields ?? []) {
      const have = out.get(f.key);
      out.set(f.key, have ? { ...have, required: have.required || f.required } : f);
    }
  }
  return [...out.values()];
}

export const totalMinutes = (services: PublicService[], ids: string[]) =>
  services.filter((s) => ids.includes(s.id)).reduce((sum, s) => sum + s.duration_minutes, 0);

/** "45 min", "1 hr", "1 hr 30 min" */
export function durationLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

// ---------------------------------------------------------------------------------------------------------------------------------
// The verified session. Kept only for this browser tab (sessionStorage): closing the tab ends it, and it never reaches another device.
// The server decides when it really ends (20 minutes, 5 bookings); this just avoids showing a form that is bound to be refused.
// ---------------------------------------------------------------------------------------------------------------------------------

export interface SavedSession {
  token: string;
  phone: string;
  needsName: boolean;
  name: string | null;
  /** The business checks dates of birth: "verify" (one is on file, it must be checked), "collect" (give one with the booking) or "off". */
  dobMode?: "off" | "verify" | "collect";
  /** The date of birth has been checked (or given) in this session. */
  dobOk?: boolean;
  /** epoch ms */
  expiresAt: number;
}

/** A little under the server's 20 minutes, so the page never offers a session that is about to be refused. */
export const SESSION_MS = 19 * 60 * 1000;

const key = (slug: string) => `rc:book:${slug}`;

export function loadSession(slug: string, now: number = Date.now()): SavedSession | null {
  try {
    const raw = sessionStorage.getItem(key(slug));
    if (!raw) return null;
    const s = JSON.parse(raw) as SavedSession;
    if (typeof s.token !== "string" || typeof s.expiresAt !== "number" || s.expiresAt <= now) {
      sessionStorage.removeItem(key(slug));
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function saveSession(slug: string, s: Omit<SavedSession, "expiresAt">, now: number = Date.now()): SavedSession {
  const full = { ...s, expiresAt: now + SESSION_MS };
  try {
    sessionStorage.setItem(key(slug), JSON.stringify(full));
  } catch {
    // Not saved: the session lives in memory for this page view only.
  }
  return full;
}

export function clearSession(slug: string): void {
  try {
    sessionStorage.removeItem(key(slug));
  } catch {
    // nothing to clear
  }
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------------------------------------------------------------

/** What to tell the customer for each answer the booking function can give. Anything unknown gets the generic line. */
export function errorMessage(code: string, detail?: string | null, attemptsLeft?: number | null): string {
  switch (code) {
    case "not_a_mobile":
      return "Enter an Australian mobile number, like 0412 345 678.";
    case "wait":
      return "A code was only just sent. Please wait a moment before asking for another.";
    case "too_many_codes":
      return "Too many codes have been requested. Please try again later, or phone us.";
    case "text_failed":
      return "We could not send the text message. Please check the number and try again.";
    case "bot_check_failed":
      return "Please complete the check below and try again.";
    case "wrong_code":
      return attemptsLeft != null && attemptsLeft > 0 ? `That code is not right. You have ${attemptsLeft} ${attemptsLeft === 1 ? "try" : "tries"} left.` : "That code is not right.";
    case "too_many_attempts":
      return "Too many wrong codes. Please ask for a new code.";
    case "no_code":
      return "That code has expired. Please ask for a new code.";
    case "invalid_session":
      return "For your security we need to check your mobile again. Please ask for a new code.";
    case "session_limit":
      return "That is the most bookings we can make in one go. Please ask for a new code to book more.";
    case "slot_unavailable":
      return "Sorry, that time has just been taken. Please choose another.";
    case "time_not_allowed":
      return "That time can no longer be booked online. Please choose another.";
    case "too_late_to_change":
      return "It is too close to the booking to change it online. Please phone us.";
    case "booking_not_found":
      return "We could not find that booking. It may already have been changed.";
    case "name_required":
      return "Please enter your name.";
    case "dob_required":
      return "Please confirm your date of birth first.";
    case "dob_invalid":
      return "Please enter a valid date of birth.";
    case "wrong_dob":
      return attemptsLeft != null && attemptsLeft > 0 ? `That date of birth does not match our records. You have ${attemptsLeft} ${attemptsLeft === 1 ? "try" : "tries"} left.` : "That date of birth does not match our records.";
    case "dob_too_many":
      return "That date of birth did not match, so for your security we have stopped. Please ask for a new code, or phone us.";
    case "intake_required":
      return detail ? `Please answer: ${detail}.` : "Please answer all the required questions.";
    case "intake_too_long":
      return detail ? `Your answer to "${detail}" is too long.` : "One of your answers is too long.";
    case "service_unavailable":
    case "staff_unavailable":
      return "That choice is no longer available online. Please choose again.";
    case "online_booking_unavailable":
      return "Online booking is not available right now.";
    case "network":
      return "We could not reach the booking service. Please check your connection and try again.";
    default:
      return "Something went wrong. Please try again, or phone us.";
  }
}

/** Services grouped by category (titles A-Z, services in the order given); services without a category come last under a null title. */
export function groupByCategory<T extends { category?: string | null }>(services: T[]): { title: string | null; items: T[] }[] {
  const groups = new Map<string | null, T[]>();
  for (const s of services) {
    const key = s.category?.trim() || null;
    groups.set(key, [...(groups.get(key) ?? []), s]);
  }
  return [...groups.entries()].sort(([a], [b]) => (a === null ? 1 : b === null ? -1 : a.localeCompare(b))).map(([title, items]) => ({ title, items }));
}
